import * as React from 'react';
import * as PropTypes from 'prop-types';
import {
  MobileDetection,
  APIRequest,
  TokenizedCreditCardFields,
  saveCreditCard,
  shouldUseAdyenCheckoutAPI
} from '@reverbdotcom/discovery-ui';
import ErrorMessage from '../shared/error_message';
import BillingAddressFields from '../addresses/billing_address_fields';
import AdyenCreditCardFormFields from './adyen_credit_card_form_fields';
import UiUtil from '../utils/ui';
import UrlHelpers from '../shared/url_helpers';
import { validateRequiredFields, scrollToError } from '../forms';

const cannotDeleteCardErrorMessage = I18n.t(
  'js.creditCards.creditCardForm.cannotDeleteCardHtml',
  {
    linkOpen: `<a href=${UrlHelpers.statementsPath} rel='noreferrer noopener' target='_blank'>`,
    linkClose: '</a>',
  },
);

class AdyenCreditCardForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      creditCardData: {
        id: props.creditCard.creditCardId,
        cardholderName: props.creditCard.cardholderName,
        expirationMonth: props.creditCard.expirationMonth,
        expirationYear: props.creditCard.fullExpirationYear,
      },
      tokenizedFields: {},
      errorMessage: '',
      creditCardErrorMessage: '',
      enabledSaveButton: true,
      showDeleteCreditCardConfirm: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.saveCard = this.saveCard.bind(this);
    this.nameForInput = this.nameForInput.bind(this);
    this.billingAddressNameForInput = this.billingAddressNameForInput.bind(this);
    this.setCreditCardFormRef = this.setCreditCardFormRef.bind(this);
    this.saveErrorMessageRef = this.saveErrorMessageRef.bind(this);
    this.showDeleteCreditCardConfirm = this.showDeleteCreditCardConfirm.bind(this);
    this.hideDeleteCreditCardConfirm = this.hideDeleteCreditCardConfirm.bind(this);
    this.handleDeleteCreditCardConfirm = this.handleDeleteCreditCardConfirm.bind(this);
    this.handleCancelEditing = this.handleCancelEditing.bind(this);
    this.onTokenizedFieldUpdate = this.onTokenizedFieldUpdate.bind(this);
  }

  componentDidUpdate() {
    if (this.state.errorMessage) {
      if (MobileDetection.isMobileScreenWidth()) {
        UiUtil.scrollToElement($(this.errorMessageRef));
      }
    }
  }

  onTokenizedFieldUpdate(newTokenizedFields) {
    const tokenizedFields = Object.assign({}, this.state.tokenizedFields, newTokenizedFields);
    this.setState({ tokenizedFields });
  }

  get cardId() {
    return this.state.creditCardData.id;
  }

  get billingAddressPrefix() {
    return this.nameForInput('billing_address');
  }

  setCreditCardFormRef(e) {
    this.creditCardFormRef = e;
  }

  handleChange(e) {
    const creditCardData = Object.assign(
      {},
      this.state.creditCardData,
      { [e.target.id]: e.target.value },
    );

    this.setState({ creditCardData });
  }

  saveErrorMessageRef(e) {
    this.errorMessageRef = e;
  }

  displayErrorMessage() {
    if (this.state.errorMessage) {
      return (
        <div ref={this.saveErrorMessageRef}>
          <ErrorMessage
            alertStyle="alert-error"
            messageWithLinks={this.state.errorMessage}
          />
        </div>
      );
    }

    return null;
  }

  displayCreditCardErrorMessage() {
    if (this.state.creditCardErrorMessage) {
      return (
        <ErrorMessage
          alertStyle="alert-error"
          message={this.state.creditCardErrorMessage}
        />
      );
    }

    return null;
  }

  handleCancelEditing() {
    if (!this.isCreatingNewCreditCard()) {
      this.props.cancelEditing();
    } else {
      this.props.handleUpdate();
    }
  }

  displayCancelEditLink() {
    return (
      <button
        className="button-as-link address-form__actions__tertiary"
        id="cancel_edit"
        onClick={this.handleCancelEditing}
      >
        {I18n.t('js.creditCards.creditCardForm.cancel')}
      </button>
    );
  }

  nameForInput(fieldName) {
    if (this.props.formObjectName) {
      return `${this.props.formObjectName}[${fieldName}]`;
    }
    return null;
  }

  billingAddressNameForInput(fieldName) {
    return `${this.billingAddressPrefix}[${fieldName}]`;
  }

  isCreatingNewCreditCard() {
    return !this.cardId;
  }

  handleDeleteCreditCardConfirm(e) {
    e.preventDefault();
    APIRequest.V3.destroy(UrlHelpers.myCreditCardPath(this.cardId)).then(() => {
      this.props.handleUpdate();
    }).fail(() => {
      this.setState({
        errorMessage: cannotDeleteCardErrorMessage,
        showDeleteCreditCardConfirm: false,
      });
    });

    return true;
  }

  showDeleteCreditCardConfirm() {
    this.setState({ showDeleteCreditCardConfirm: true });
  }

  hideDeleteCreditCardConfirm() {
    this.setState({ showDeleteCreditCardConfirm: false });
  }

  displayDeleteOptionWithConfirm() {
    if (this.state.showDeleteCreditCardConfirm) {
      return (
        <div className="address-form__actions__secondary">
          <span className="small mb-half">
            {I18n.t('js.creditCards.creditCardForm.confirmDelete')}
          </span>
          <button
            id="confirm_delete"
            className="button-as-link ml-half"
            data-confirm-delete-credit-card-id={this.cardId}
            onClick={this.handleDeleteCreditCardConfirm}
          >
            {I18n.t('js.creditCards.creditCardForm.yes')}
          </button>
          <button
            className="button-as-link ml-half"
            onClick={this.hideDeleteCreditCardConfirm}
          >
            {I18n.t('js.creditCards.creditCardForm.no')}
          </button>
        </div>
      );
    }

    return (
      <div className="address-form__actions__secondary">
        <button
          id="delete_card"
          data-delete-credit-card-id={this.cardId}
          onClick={this.showDeleteCreditCardConfirm}
          className="button-as-link text-poor"
        >
          {I18n.t('js.creditCards.creditCardForm.deleteCard')}
        </button>
      </div>
    );
  }

  displayDeleteLink() {
    if (this.isCreatingNewCreditCard()) {
      return null;
    }

    return this.displayDeleteOptionWithConfirm();
  }

  requiredFieldsPresent() {
    return validateRequiredFields($(this.creditCardFormRef));
  }

  saveCard(billingAddressProperties) {
    if (!this.requiredFieldsPresent()) {
      return scrollToError();
    }

    this.setState({
      enabledSaveButton: false,
    });

    return saveCreditCard(
      this.state.creditCardData,
      this.state.tokenizedFields,
      billingAddressProperties,
    ).then(() => {
      this.props.handleUpdate();
    }).fail((response) => {
      const { message } = JSON.parse(response.responseText);
      this.setState({
        errorMessage: message,
        enabledSaveButton: true,
      });
    });
  }

  renderCreditCardFields() {
    if (shouldUseAdyenCheckoutAPI() && this.isCreatingNewCreditCard()) {
      return (
        <div>
          <div className="scaling-mb-1">
            <dt>
              <label htmlFor="cardholderName">
                {I18n.t('js.creditCards.creditCardForm.nameOnCard')}
              </label>
            </dt>
            <dd>
              <input
                id="cardholderName"
                type="text"
                autoComplete="name"
                className="wide required"
                value={this.state.creditCardData.cardholderName}
                onChange={this.handleChange}
              />
            </dd>
          </div>
          <TokenizedCreditCardFields
            onTokenizedFieldUpdate={this.onTokenizedFieldUpdate}
            buildAdyenCheckout={this.props.buildAdyenCheckout}
          />
        </div>
      );
    }

    return (
      <AdyenCreditCardFormFields
        onChange={this.handleChange}
        isCreatingNewCreditCard={this.isCreatingNewCreditCard()}
        cardholderName={this.state.creditCardData.cardholderName}
        expirationMonth={this.state.creditCardData.expirationMonth}
        expirationYear={this.state.creditCardData.expirationYear}
        cvv={this.state.creditCardData.cvv}
        cardType={this.props.creditCard.cardType}
        lastFour={this.props.creditCard.lastFour}
      />
    );
  }

  render() {
    return (
      <div
        data-credit-card-form
        ref={this.setCreditCardFormRef}
      >
        {this.displayErrorMessage()}
        {this.renderCreditCardFields()}

        <BillingAddressFields
          streetAddress={this.props.creditCard.streetAddress}
          extendedAddress={this.props.creditCard.extendedAddress}
          locality={this.props.creditCard.locality}
          region={this.props.creditCard.region}
          postalCode={this.props.creditCard.postalCode}
          countryCode={this.props.creditCard.countryCode}
          countriesForSelect={this.props.countriesForSelect}
          nameForInput={this.billingAddressNameForInput}
          enabledSaveButton={this.state.enabledSaveButton}
          handleSaveClick={this.saveCard}
        >
          <div className="address-form__actions__options-wrapper">
            {this.displayCancelEditLink()}
            {this.displayDeleteLink()}
          </div>
        </BillingAddressFields>
      </div>
    );
  }
}

AdyenCreditCardForm.propTypes = {
  creditCard: PropTypes.shape({
    creditCardId: PropTypes.string,
    cardType: PropTypes.string,
    lastFour: PropTypes.string,
    cardholderName: PropTypes.string,
    expirationMonth: PropTypes.string,
    cvv: PropTypes.string,
    expirationYear: PropTypes.string,
    fullExpirationYear: PropTypes.string,
    streetAddress: PropTypes.string,
    extendedAddress: PropTypes.string,
    locality: PropTypes.string,
    region: PropTypes.string,
    postalCode: PropTypes.string,
    countryCode: PropTypes.string,
  }).isRequired,
  countriesForSelect: PropTypes.arrayOf(PropTypes.object).isRequired,
  cancelEditing: PropTypes.func.isRequired,
  handleUpdate: PropTypes.func.isRequired,
  formObjectName: PropTypes.string.isRequired,
  buildAdyenCheckout: PropTypes.func,
};

AdyenCreditCardForm.defaultProps = {
  buildAdyenCheckout: undefined,
};

export default AdyenCreditCardForm;



// WEBPACK FOOTER //
// ./javascripts/components/credit_cards/adyen_credit_card_form.jsx